import { ComponentProps } from "react";
import styled from "styled-components";

import type {
  MedicalInstitution,
  MedicalInstitutionMember,
  ClientCertificate,
  Contract,
  EmailContactSummary,
} from "../../../types";
import { Typography } from "../../atoms/Typography";
import { List, ListItem } from "../../atoms/List";
import { Accordion } from "../../organisms/Accordion";
import { MedicalInstitutionMemberList } from "../../organisms/MedicalInstitutionMemberList";
import { PageBody } from "../PageBody";
import { ClientCertificate as ClientCertificateContainer } from "../../../containers/organisms/ClientCertificate";
import { User } from "../../../hooks/useAuthentication";
import { USER_ROLE, UserRole } from "../../../utils";
import { ContractManagerList } from "../../organisms/ContractManagerList";
import { ContractResponsiblePersonList } from "../../organisms/ContractResponsiblePersonList";
import { ContractNotificationRecipientList } from "../../organisms/ContractNotificationRecipientList";
import { ContractList, ContractWithDates } from "../../organisms/ContractList";
import { BillingAccountList } from "../../organisms/BillingAccountList";
import { Button } from "../../atoms/Button";
import { EmailContactSummaryList } from "../../organisms/EmailContactSummaryList";

/**
 * 医療機関詳細ページ
 */

export type MedicalInstitutionProps = {
  medicalInstitution: MedicalInstitution & { apsUrl: string };
  medicalInstitutionMembers: MedicalInstitutionMember[];
  contracts: Contract[];
  contractsIncludingCanceled: ContractWithDates[];
  emailContracts: EmailContactSummary[];
  onCancelContract: (contractId: string) => void;
  onCancelMedicalInstitution: () => void;
  allContractsCanceled: boolean;
  refetchMedicalInstitutionMembers: () => void;
  onContractManagerResendWelcomeMailButtonClick: (contractId: string) => void;
  onResponsiblePersonResendWelcomeMailButtonClick: (contractId: string) => void;
  clientCertificate: ClientCertificate;
  userData: User | undefined;
};

const ContentWrapper = styled.div`
  padding: 0 40px;
`;

const Inner = styled.div`
  padding: 40px 0 20px;
  display: flex;
`;

const AccordionWrapper = styled.div`
  padding: 40px 0;
  display: flex;
  flex-flow: column;
`;

const LeftBlock = styled.div`
  flex: 1;
  padding-right: 40px;
`;

const MemberIconAndName = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-right: 20px;
  }
`;

const RightBlock = styled.div`
  flex: 1;
  padding-left: 40px;
`;

const PaddingRightWrapper = styled.div`
  width: 100%;
  padding-right: 20px;
`;

const CancelMedicalInstitutionButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  width: 200px;
  height: 30px;
  margin-left: auto;
`;

const SummaryWrapper = styled.div<{
  $padding?: string;
}>`
  padding: ${(props) => props.$padding || "16px 0"};
`;

const TextRightTypography = (props: ComponentProps<typeof Typography>) => (
  <PaddingRightWrapper>
    <Typography textRight size={2}>
      {props.children}
    </Typography>
  </PaddingRightWrapper>
);

export function MedicalInstitutionDetailPage({
  medicalInstitution,
  medicalInstitutionMembers,
  contracts,
  contractsIncludingCanceled,
  emailContracts,
  onCancelContract,
  onCancelMedicalInstitution,
  allContractsCanceled,
  refetchMedicalInstitutionMembers,
  onContractManagerResendWelcomeMailButtonClick,
  onResponsiblePersonResendWelcomeMailButtonClick,
  clientCertificate,
  userData,
}: MedicalInstitutionProps) {
  const billingAccounts = medicalInstitution.billingAccount
    ? [medicalInstitution.billingAccount]
    : [];

  return (
    <>
      <PageBody title="医療機関詳細">
        <>
          <ContentWrapper>
            <Inner>
              <LeftBlock>
                <MemberIconAndName>
                  <img
                    src={medicalInstitution.memberImage}
                    width="72"
                    height="72"
                    alt={"user_icon"}
                  />
                  <Typography size={5} breakWord>
                    {medicalInstitution.name}
                  </Typography>
                </MemberIconAndName>
              </LeftBlock>
            </Inner>
          </ContentWrapper>

          <ContentWrapper>
            <Inner>
              <LeftBlock>
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      id
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.id}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      電話番号
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.phoneNumber}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      セットアップ状態
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.isSetupFinished ? "完了" : "未完了"}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      契約状況
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.deletedAt ? "退会済み" : "契約中"}
                    </TextRightTypography>
                  </ListItem>
                </List>
              </LeftBlock>
              <RightBlock>
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      郵便番号
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.postalCode}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      住所
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.prefecture}
                      {medicalInstitution.city}
                      {medicalInstitution.otherAddress}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      APS URL
                    </Typography>
                    <TextRightTypography>
                      {medicalInstitution.apsUrl}
                    </TextRightTypography>
                  </ListItem>
                </List>
              </RightBlock>
            </Inner>
          </ContentWrapper>
          <ContentWrapper>
            <AccordionWrapper>
              <Accordion title={"契約"}>
                <>
                  <ContractList
                    contracts={contractsIncludingCanceled}
                    onCancelClick={onCancelContract}
                    isCsUser={userData?.userRole === USER_ROLE.CS_USER}
                  />
                  {userData?.userRole === USER_ROLE.CS_USER &&
                    !medicalInstitution.deletedAt && (
                      <CancelMedicalInstitutionButtonWrapper>
                        <Button
                          buttonType="danger"
                          onClick={onCancelMedicalInstitution}
                          disabled={!allContractsCanceled}
                        >
                          APSの退会処理をする
                        </Button>
                      </CancelMedicalInstitutionButtonWrapper>
                    )}
                </>
              </Accordion>
            </AccordionWrapper>
          </ContentWrapper>
          {userData?.userRole === USER_ROLE.CS_USER &&
            !medicalInstitution.deletedAt && (
              <ContentWrapper>
                <AccordionWrapper>
                  <Accordion title={"クライアント証明書"}>
                    <ClientCertificateContainer
                      clientCertificate={clientCertificate}
                    />
                  </Accordion>
                </AccordionWrapper>
              </ContentWrapper>
            )}
          {!medicalInstitution.deletedAt && (
            <>
              <ContentWrapper>
                <AccordionWrapper>
                  <Accordion title={"医療機関メンバー"}>
                    <MedicalInstitutionMemberList
                      medicalInstitutionMembers={medicalInstitutionMembers}
                      refetchMedicalInstitutionMembers={
                        refetchMedicalInstitutionMembers
                      }
                      userRole={userData?.userRole as UserRole}
                    />
                  </Accordion>
                </AccordionWrapper>
              </ContentWrapper>

              <ContentWrapper>
                <AccordionWrapper>
                  <Accordion title={"製品管理責任者"}>
                    <ContractManagerList
                      contracts={contracts}
                      onManagerClick={
                        onContractManagerResendWelcomeMailButtonClick
                      }
                    />
                  </Accordion>
                </AccordionWrapper>
              </ContentWrapper>

              <ContentWrapper>
                <AccordionWrapper>
                  <Accordion
                    title={
                      "[新] 製品管理責任 / 通知連絡先 / 連絡先別の関連製品"
                    }
                  >
                    <SummaryWrapper>
                      <Typography as="h3" bold size={2}>
                        製品管理責任者
                      </Typography>
                      <ContractResponsiblePersonList
                        contracts={contracts}
                        onManagerClick={
                          onResponsiblePersonResendWelcomeMailButtonClick
                        }
                      />
                    </SummaryWrapper>
                    <SummaryWrapper>
                      <Typography as="h3" bold size={2}>
                        通知連絡先
                      </Typography>
                      <ContractNotificationRecipientList
                        contracts={contracts}
                      />
                    </SummaryWrapper>
                    <SummaryWrapper>
                      <Typography as="h3" bold size={2}>
                        連絡先別の関連製品
                      </Typography>
                      <EmailContactSummaryList emailContacts={emailContracts} />
                    </SummaryWrapper>
                  </Accordion>
                </AccordionWrapper>
              </ContentWrapper>

              <ContentWrapper>
                <AccordionWrapper>
                  <Accordion title={"決済担当者"}>
                    <BillingAccountList billingAccounts={billingAccounts} />
                  </Accordion>
                </AccordionWrapper>
              </ContentWrapper>
            </>
          )}
        </>
      </PageBody>
    </>
  );
}
